import { useEffect, useRef, useCallback } from 'react';

/**
 * Hook for horizontal resizing of elements with mouse drag.
 * @param {Object} options
 * @param {React.RefObject} options.targetRef - Ref to the resizable element
 * @param {React.RefObject} options.containerRef - Ref to the container element
 * @param {number} [options.edgeWidth=3] - Width of the edge detection zone in pixels
 * @param {number} [options.minWidth=350] - Minimum width in pixels
 * @param {number} [options.minPercentage=30] - Minimum percentage
 * @param {number} [options.maxPercentage=70] - Maximum percentage
 * @param {Function} [options.onResizeStart] - Called once when resize starts
 * @param {Function} [options.onResize] - Called on each resize with { percentageList, percentageDetail }
 * @param {boolean} [options.enabled=true] - Whether the resize functionality is enabled (useful for modals/conditional rendering)
 */
const useHorizontalResize = ({
    targetRef,
    containerRef,
    edgeWidth = 3,
    minWidth = 350,
    minPercentage = 30,
    maxPercentage = 70,
    onResizeStart,
    onResize,
    enabled = true,
}) => {
    const isResizing = useRef(false);
    const isHovering = useRef(false);
    const hasTriggeredStart = useRef(false);
    const rafId = useRef(null);
    const overlayRef = useRef(null);

    useEffect(() => {
        const target = targetRef.current;
        const container = containerRef.current;
        if (!enabled || !target || !container) return;

        // Create invisible overlay for resize zone
        const overlay = document.createElement('div');
        overlay.className = 'fm-resize-handle';
        overlay.style.cssText = `
            position: absolute;
            top: 0;
            right: 0;
            width: ${edgeWidth}px;
            height: 100%;
            cursor: ew-resize;
            z-index: 100;
        `;
        target.appendChild(overlay);
        overlayRef.current = overlay;

        const applyHighlight = () => {
            target.style.boxShadow = 'inset -2px 0 0 var(--actionMajor-500)';
        };

        const removeHighlight = () => {
            target.style.boxShadow = '';
        };

        const handleMouseEnter = () => {
            isHovering.current = true;
            applyHighlight();
        };

        const handleMouseLeave = () => {
            isHovering.current = false;
            if (!isResizing.current) {
                removeHighlight();
            }
        };

        const handleMouseDown = (e) => {
            isResizing.current = true;
            document.body.style.cursor = 'ew-resize';
            document.body.style.userSelect = 'none';
            applyHighlight();
            e.preventDefault();
            e.stopPropagation();

            if (!hasTriggeredStart.current) {
                hasTriggeredStart.current = true;
                onResizeStart?.();
            }
        };

        const handleMouseMove = (e) => {
            if (!isResizing.current) return;

            if (rafId.current) cancelAnimationFrame(rafId.current);

            rafId.current = requestAnimationFrame(() => {
                const totalWidth = container.offsetWidth;
                const containerRect = container.getBoundingClientRect();
                let newWidth = e.clientX - containerRect.left;

                if (newWidth < minWidth) newWidth = minWidth;

                let percentageList = (100 / totalWidth) * newWidth;

                if (percentageList < minPercentage) {
                    percentageList = minPercentage;
                } else if (percentageList > maxPercentage) {
                    percentageList = maxPercentage;
                }

                const percentageDetail = 100 - percentageList;

                target.style.width = `${percentageList}%`;
                onResize?.({ percentageList, percentageDetail });
            });
        };

        const handleMouseUp = () => {
            if (isResizing.current) {
                isResizing.current = false;
                document.body.style.cursor = '';
                document.body.style.userSelect = '';
                if (!isHovering.current) {
                    removeHighlight();
                }
            }
        };

        overlay.addEventListener('mouseenter', handleMouseEnter);
        overlay.addEventListener('mouseleave', handleMouseLeave);
        overlay.addEventListener('mousedown', handleMouseDown);
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            overlay.removeEventListener('mouseenter', handleMouseEnter);
            overlay.removeEventListener('mouseleave', handleMouseLeave);
            overlay.removeEventListener('mousedown', handleMouseDown);
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            if (rafId.current) cancelAnimationFrame(rafId.current);
            removeHighlight();
            if (overlayRef.current && target.contains(overlayRef.current)) {
                target.removeChild(overlayRef.current);
            }
        };
    }, [
        targetRef,
        containerRef,
        edgeWidth,
        minWidth,
        minPercentage,
        maxPercentage,
        onResizeStart,
        onResize,
        enabled,
    ]);
};

export default useHorizontalResize;
